import type { Brand as MelwaysBrand } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { useMemo } from 'react';

import { createSuggestion } from 'src/pages/contact-us/App/components/EnquiryForm/helpers/suggestionHelper';
import type { UrlWithPlaceholder } from 'src/pages/contact-us/App/components/EnquiryForm/helpers/suggestionMapping';
import { useConfig } from 'src/shared/hooks';
import type { SuggestionType } from 'src/shared/types';
import { getCorrectlyCasedBrandName } from 'src/utils/brandUtils';

import translations from './.vocab';

// Define a local Brand type that includes seekAnz
type Brand = MelwaysBrand | 'seekAnz';

export interface SubItem {
  type: string;
  name: string;
  label: string;
  required: boolean;
  options?: Array<{
    name: string;
    label: string;
    value?: string;
  }>;
}

export interface Reason {
  reason: string;
  rspToggleRequired?: boolean;
  brand?: Brand[];
  suggestion?: SuggestionType;
  subItems?: SubItem[];
  placeholder?: UrlWithPlaceholder;
}

export type Subject = Record<string, Reason[]>;

// Helper function to get the correct profile subject key based on brand
const getProfileSubjectKey = (brand: Brand): string => {
  switch (brand) {
    case 'seekAnz':
    case 'seek':
      return 'SEEK Profile';
    default:
      return 'Jobstreet Profile';
  }
};

// Helper function to get the correct profile reason key based on brand
const getProfileReasonKey = (brand: Brand): string => {
  switch (brand) {
    case 'seekAnz':
    case 'seek':
      return 'What is a SEEK Profile';
    default:
      return 'What is a Jobstreet Profile';
  }
};

export const useSubjects = (brand: Brand, isAnz: boolean) => {
  const { t } = useTranslations(translations);
  const { country } = useConfig();
  const countryCode = country.toUpperCase();

  const subjectData: Subject = useMemo(() => {
    const profileSubjectKey = getProfileSubjectKey(brand);
    const profileReasonKey = getProfileReasonKey(brand);

    // Use the translated version for display, but the explicit key for logic
    const correctlyCasedBrand = getCorrectlyCasedBrandName(
      brand as MelwaysBrand,
    );
    const profileSubjectDisplay = t('{brandName} Profile', {
      brandName: correctlyCasedBrand,
    });
    const profileReasonDisplay = t('What is a {brandName} Profile', {
      brandName: correctlyCasedBrand,
    });

    const data = {
      // Use the display name for the subject key in the form
      [profileSubjectDisplay]: [
        {
          // Use the display name for the reason dropdown
          reason: profileReasonDisplay,
          // Use the explicit keys when calling createSuggestion
          suggestion: createSuggestion(
            profileSubjectKey,
            profileReasonKey,
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Create or Upload a resume'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'Create or Upload a resume',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Edit or Delete a resume'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'Edit or Delete a resume',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Update my profile'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'Update my profile',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('My profile visibility'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'My profile visibility',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Delete my profile'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'Delete my profile',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Profile sent with application'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'Profile sent with application',
            countryCode,
            brand,
          ),
        },
        { reason: t('Other Issues') },
      ],
      // Keep other subjects as they were, using t()
      [t('Login / Password')]: [
        {
          reason: t('Forgot my username or password'),
          suggestion: createSuggestion(
            'Login / Password',
            'Forgot my username or password',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Sign in error'),
          suggestion: createSuggestion(
            'Login / Password',
            'Sign in error',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('My account has been blocked'),
          suggestion: createSuggestion(
            'Login / Password',
            'My account has been blocked',
            countryCode,
            brand,
          ),
        },
        {
          reason: t("I didn't receive my password reset e-mail"),
          suggestion: createSuggestion(
            'Login / Password',
            "I didn't receive my password reset e-mail",
            countryCode,
            brand,
          ),
        },
        { reason: t('Other Issues') },
      ],
      [t('My Account')]: [
        {
          reason: t('Update account details'),
          suggestion: createSuggestion(
            'My Account',
            'Update account details',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Update my e-mail address'),
          suggestion: createSuggestion(
            'My Account',
            'Update my e-mail address',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Registration'),
          suggestion: createSuggestion(
            'My Account',
            'Registration',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Applied Jobs'),
          suggestion: createSuggestion(
            'My Account',
            'Applied jobs',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Saved jobs'),
          suggestion: createSuggestion(
            'My Account',
            'Saved jobs',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Delete account'),
          suggestion: createSuggestion(
            'My Account',
            'Delete account',
            countryCode,
            brand,
          ),
        },
        { reason: t('Other Issues') },
      ],
      [t('Job Search')]: [
        {
          reason: t("How does {brandName}'s search engine work?", {
            brandName: getCorrectlyCasedBrandName(brand as MelwaysBrand),
          }),
          brand: [brand],
        },
        {
          reason: t('How to search for a job'),
          suggestion: createSuggestion(
            'Job Search',
            'How to search for a job',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Job search issues/error'),
          suggestion: createSuggestion(
            'Job Search',
            'Job search issues/error',
            countryCode,
            brand,
          ),
        },
        { reason: t('Location search'), brand: ['seek'] },
        { reason: t('Salary'), brand: ['seek'] },
        { reason: t('Other Issues') },
      ],
      [t('Applying for a job')]: [
        {
          reason: t('Application error'),
          suggestion: createSuggestion(
            'Applying for a job',
            'Application error',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('How to apply'),
          suggestion: createSuggestion(
            'Applying for a job',
            'How to apply',
            countryCode,
            brand,
          ),
        },
        { reason: t('Certsy'), brand: ['seek'] },
        {
          reason: t('Privacy & data protection'),
          suggestion: createSuggestion(
            'Applying for a job',
            'Privacy & data protection',
            countryCode,
            brand,
          ),
        },
        { reason: t('International application / Visa queries') },
        {
          reason: t('Cover letter'),
          suggestion: createSuggestion(
            'Applying for a job',
            'Cover letter',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Withdrawal of application'),
          suggestion: createSuggestion(
            'Applying for a job',
            'Withdrawal of application',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Follow up application'),
          suggestion: createSuggestion(
            'Applying for a job',
            'Follow up application',
            countryCode,
            brand,
          ),
        },
        { reason: t('Other Issues') },
      ],
      [t('Report Issues')]: [
        {
          reason: t('Report a suspicious ad'),
          suggestion: createSuggestion(
            'Report Issues',
            'Report a suspicious Ad',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Report a suspicious hirer'),
          suggestion: createSuggestion(
            'Report Issues',
            'Report a suspicious Hirer',
            countryCode,
            brand,
          ),
        },
      ],
      [t('Email Issues')]: [
        {
          reason: t("I've stopped receiving e-mails"),
          suggestion: createSuggestion(
            'Email Issues',
            "I've stopped receiving e-mails",
            countryCode,
            brand,
          ),
        },
        {
          reason: t('I need to update my e-mail address'),
          suggestion: createSuggestion(
            'Email Issues',
            'I need to update my e-mail address',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Unsubscribe from emails'),
          suggestion: createSuggestion(
            'Email Issues',
            'Unsubscribe from emails',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Job recommendation emails'),
          suggestion: createSuggestion(
            'Email Issues',
            'Job recommendation emails',
            countryCode,
            brand,
          ),
        },
        { reason: t('Weekly Round-up'), brand: ['seek'] },
        { reason: t('Other Issues') },
      ],
      ...(isAnz
        ? {
            [t('General feedback')]: [
              { reason: t('Product feedback') },
              { reason: t('Company Reviews') },
            ],
          }
        : {}),
      [t('Other Issues')]: [
        {
          reason: t('Sign in error'),
          suggestion: createSuggestion(
            'Login / Password',
            'Sign in error',
            countryCode,
            brand,
          ),
        },
        { reason: t('I have technical issues') },
        { reason: t('Website/system is down') },
        { reason: t('Mobile app issue') },
        { reason: t('Other Issues') },
      ],
    };

    return data as Subject;
  }, [brand, isAnz, countryCode, t]);

  return { subjectData };
};
